"use client";

import * as React from "react";

import { useSliders, type UseSlidersOptions } from "@/hooks/slider";
import type { FetchApiError, StrapiCollectionResponse, StrapiEntity } from "@/lib/strapi";
import type { SliderAttributes } from "@/services/slider";

export type SlidersStoreValue = {
  sliders: Array<StrapiEntity<SliderAttributes>>;
  data: StrapiCollectionResponse<SliderAttributes> | null;
  error: FetchApiError | null;
  isLoading: boolean;
  refetch: () => void;
};

const SlidersStoreContext = React.createContext<SlidersStoreValue | null>(null);

export function SlidersProvider({
  children,
  initialData,
  ...options
}: React.PropsWithChildren<UseSlidersOptions & { initialData?: StrapiCollectionResponse<SliderAttributes> | null }>): React.JSX.Element {
  const state = useSliders({ ...options, initialData });

  const value: SlidersStoreValue = {
    sliders: state.sliders,
    data: state.data,
    error: state.error,
    isLoading: state.isLoading,
    refetch: state.refetch,
  };

  return <SlidersStoreContext.Provider value={value}>{children}</SlidersStoreContext.Provider>;
}

export function useSlidersStore(): SlidersStoreValue {
  const value = React.useContext(SlidersStoreContext);
  if (!value) {
    throw new Error("useSlidersStore must be used within SlidersProvider");
  }
  return value;
}
